import {
  CLOUDINARY_BASE_URL,
  getOptimizedUrl,
  transformImageUrl,
} from '../../services/CloudinaryService'
import {
  FILTERS,
  FRAME_COLORS,
  MATERIAL_TYPES,
  MATTING,
  TILE_CONTENT,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'

// The canvas centerpieces are slightly bigger than the frameless centerpieces, because they add
// 1 inch from each side due to the canvas wraps (so they are actually 27"x10"). Therefore, the resolutions
// are also different (8100x3000 compared to 7635x2595). To create centerpieces in the
// same way, we take the bigger centerpieces for canvas and crop the correct size for the frameless centerpieces.
export const CENTERPIECES_PATH = 'assets/canvas/centerpieces'
export const CENTERPIECES_THUMB_PATH = 'centerpieces'

// Calculated by taking the old resolution and
export const FRAMELESS_CENTERPIECE_RELATIVE_CROP_RECT = {
  width: 0.942, // 7635 / 8100
  height: 0.865, // 2595 / 3000
  x: 0.028, // (1 - 7635 / 8100) / 2
  y: 0.067, // (1 - 2595/3000) / 2
}
export const FRAMELESS_CENTERPIECE_CROP_RECT = {
  width: 7635,
  height: 2595,
  x: 232.5, // (8100 - 7635) / 2
  y: 202.5, // (3000 - 2595) / 2
}

export const CANVAS_SIDES_OFFSETS = {
  left: FRAMELESS_CENTERPIECE_CROP_RECT.x,
  top: FRAMELESS_CENTERPIECE_CROP_RECT.y,
}

const ImageTypes = {
  THUMB: 'thumbnail',
  ORIGINAL: 'original',
}

export const CENTERPIECE_IMAGE_DIMENSIONS = { width: 1416, height: 472 }

export function createCenterpieceTile({
  imageUrl,
  file,
  title,
  thumbUrl,
  ...params
}: {
  imageUrl: string
  file?: File
  title: string
  thumbUrl?: string
}) {
  thumbUrl =
    thumbUrl ||
    transformImageUrl(imageUrl, undefined, CENTERPIECE_IMAGE_DIMENSIONS, true)

  const fileUrl = file && URL.createObjectURL(file)

  return {
    ...params,
    originalUrl: imageUrl,
    localUrl: fileUrl,
    thumbUrl: fileUrl,
    lowResUrl: thumbUrl,
    highResUrl: thumbUrl,
    customStyle: {
      frameColor: FRAME_COLORS.NONE,
      matting: MATTING.NONE,
      filter: FILTERS.NONE,
    },
    centerpieceTitle: title,
    width: CENTERPIECE_IMAGE_DIMENSIONS.width,
    height: CENTERPIECE_IMAGE_DIMENSIONS.height,
    cropParams: {
      3.13: {
        x: 0,
        y: 0,
        width: CENTERPIECE_IMAGE_DIMENSIONS.width,
        height: CENTERPIECE_IMAGE_DIMENSIONS.height,
        zoom: 1,
      },
      2.7: {
        x: 0,
        y: 0,
        width: CENTERPIECE_IMAGE_DIMENSIONS.width,
        height: CENTERPIECE_IMAGE_DIMENSIONS.height,
        zoom: 1,
      },
    },
    tileSize: TILE_SIZES.RECTANGLE_25X8,
    materialType: MATERIAL_TYPES.FRAMELESS,
    tileContent: TILE_CONTENT.CENTERPIECE,
  }
}

const getCenterpieceUrl = (name: string, imageType = ImageTypes.THUMB) => {
  switch (imageType) {
    case ImageTypes.THUMB: {
      return getOptimizedUrl(`${CENTERPIECES_THUMB_PATH}/${name}`, {
        height: 65,
      })
    }
    case ImageTypes.ORIGINAL: {
      return `${CLOUDINARY_BASE_URL}/${CENTERPIECES_PATH}/${name}`
    }
    default: {
      return `${CLOUDINARY_BASE_URL}/${CENTERPIECES_PATH}/${name}`
    }
  }
}

const generateImageUrls = (name: string) => ({
  thumbnailUrl: getCenterpieceUrl(name, ImageTypes.THUMB),
  originalUrl: getCenterpieceUrl(name, ImageTypes.ORIGINAL),
})

export const CENTERPIECES = [
  {
    title: 'Family',
    displayName: 'Family',
    ...generateImageUrls('family'),
  },
  {
    title: 'Family - White Wood',
    displayName: 'Family',
    ...generateImageUrls('family-2'),
  },
  {
    title: 'Family - Marble',
    displayName: 'Family',
    ...generateImageUrls('family-3'),
  },
  {
    title: 'This Is Us - Wood',
    displayName: 'This Is Us',
    ...generateImageUrls('this-is-us'),
  },
  {
    title: 'This Is Us - White',
    displayName: 'This Is Us',
    ...generateImageUrls('this-is-us-2'),
  },
  {
    title: 'Home',
    displayName: 'Home',
    ...generateImageUrls('home'),
  },
  {
    title: 'Our Adventure - Black',
    displayName: 'Our Adventure',
    ...generateImageUrls('adventure-2'),
  },
  {
    title: 'Our Adventure - White',
    displayName: 'Our Adventure',
    ...generateImageUrls('adventure-3'),
  },
  {
    title: 'Every picture tells a story',
    displayName: 'Every picture tells a story',
    ...generateImageUrls('picture-story'),
  },
  {
    title: 'Together',
    displayName: 'Together',
    ...generateImageUrls('together'),
  },
  {
    title: 'Together - Black',
    displayName: 'Together',
    ...generateImageUrls('together-2'),
  },
  {
    title: 'Together We Make a Family',
    displayName: 'Together We Make a Family',
    ...generateImageUrls('together-we-make-a-family'),
  },
  {
    title: 'Love',
    displayName: 'Love',
    ...generateImageUrls('love'),
  },
  {
    title: 'Love - Blackboard',
    displayName: 'Love',
    ...generateImageUrls('love2'),
  },
  {
    title: 'Love - Floral',
    displayName: 'Love',
    ...generateImageUrls('love-3'),
  },
  {
    title: 'Love Lives Here',
    displayName: 'Love Lives Here',
    ...generateImageUrls('love-lives-here'),
  },
  {
    title: 'Grateful',
    displayName: 'Grateful',
    ...generateImageUrls('grateful'),
  },
  {
    title: 'Custom Sign',
    displayName: 'Custom Sign',
    ...generateImageUrls('custom-sign'),
    isCustom: true,
  },
]

export const BORDER_IMG_URL = getOptimizedUrl(
  `${CENTERPIECES_THUMB_PATH}/border`
)
